package com.shadowings.gero.data.lessons

import com.shadowings.gero.data.repository.Hiero
import com.shadowings.gero.domain.model.IExplanation
import com.shadowings.gero.domain.model.IQuizContent
import com.shadowings.gero.domain.model.Option
import com.shadowings.gero.domain.model.QuizText
import com.shadowings.gero.domain.model.SingleSelectionContent
import com.shadowings.gero.domain.model.UniliteralExplanation
import gero.composeapp.generated.resources.Res
import gero.composeapp.generated.resources.quiz_hiero_come_pronuncia
import gero.composeapp.generated.resources.quiz_hiero_cosa_determinativo
import gero.composeapp.generated.resources.quiz_hiero_logogramma
import gero.composeapp.generated.resources.quiz_ripasso_significato

fun buildHieroQuizContent(
    hiero: List<Hiero>,
    hieroContent: List<IExplanation>,
    languageCode: String,
    previousHieroContent: List<IExplanation>
): List<IQuizContent> {
    val newCodes = hieroContent.filterIsInstance<UniliteralExplanation>().map { it.id }
    // Per il quiz usiamo nuovi + vecchi se non arriviamo a 10
    val oldCodes = previousHieroContent.filterIsInstance<UniliteralExplanation>()
        .map { it.id }
        .filter { it !in newCodes }
    val needed = (10 - newCodes.size).coerceAtLeast(0)
    val codesForQuiz = newCodes + oldCodes.shuffled().take(needed)

    val allPronunciations = codesForQuiz
        .mapNotNull { code -> hiero.firstOrNull { it.gardinerCode == code }?.transliteration }
        .filter { it != "-" && it.isNotBlank() }
        .distinct()

    return codesForQuiz.mapNotNull { code ->
        val h = hiero.firstOrNull { it.gardinerCode == code } ?: return@mapNotNull null

        val determinativo = h.getDeterminativo(languageCode)

        val isDeterminative = (h.transliteration == "-" || h.transliteration.isBlank()) &&
                determinativo != "-" && determinativo.isNotBlank()

        if (isDeterminative) {
            val currentLessonDeterminatives = codesForQuiz
                .mapNotNull { c -> hiero.firstOrNull { it.gardinerCode == c } }
                .map { it.getDeterminativo(languageCode) }
                .filter { it != "-" && it.isNotBlank() }
                .distinct()

            val currentLessonDistractors = currentLessonDeterminatives
                .filter { it != determinativo }

            val needed = 3 - currentLessonDistractors.size
            val extraDistractors = if (needed > 0) {
                val allDeterminativesInGame = DistractorPool.hieros(hiero)
                    .map { it.getDeterminativo(languageCode) }
                    .filter { it != "-" && it.isNotBlank() }
                    .distinct()

                allDeterminativesInGame
                    .filter { it != determinativo && it !in currentLessonDistractors }
                    .shuffled()
                    .take(needed)
            } else {
                emptyList()
            }

            val distractors = (currentLessonDistractors + extraDistractors)
                .distinct()
                .shuffled()
                .take(3)

            val options = (distractors + determinativo)
                .distinct()
                .shuffled()
                .map { det -> Option(text = det, correct = det == determinativo) }

            SingleSelectionContent(
                text = Res.string.quiz_hiero_cosa_determinativo,
                detail = h.gardinerCode,
                options = options
            )
        } else if (h.transliteration.isNotBlank() && h.transliteration != "-") {
            val distractors = allPronunciations
                .filter { it != h.transliteration }
                .shuffled()
                .distinct()
                .take(3)

            val needed = 3 - distractors.size
            val extraDistractors = if (needed > 0) {
                DistractorPool.hieros(hiero).map { it.transliteration }
                    .filter { it != h.transliteration && it != "-" && it != " " && it.isNotBlank() && it !in distractors }
                    .distinct()
                    .shuffled()
                    .take(needed)
            } else {
                emptyList()
            }

            val finalDistractors = (distractors + extraDistractors).distinct().shuffled().take(3)

            val options = (finalDistractors + h.transliteration)
                .distinct()
                .shuffled()
                .map { pron -> Option(text = pron, correct = pron == h.transliteration) }

            val info = h.getIdeogramma(languageCode)
            SingleSelectionContent(
                text = Res.string.quiz_hiero_come_pronuncia,
                detail = h.gardinerCode,
                options = options,
                info = if (info != "-") {
                    QuizText.Resource(
                        Res.string.quiz_ripasso_significato,
                        listOf(h.getIdeogramma(languageCode))
                    )
                } else {
                    QuizText.Plain("")
                }
            )
        } else {
            val allLogogramsInGame = codesForQuiz
                .mapNotNull { code ->
                    hiero.firstOrNull { it.gardinerCode == code }?.getIdeogramma(languageCode)
                }
                .filter { it != "-" && it.isNotBlank() }
                .distinct()

            val distractors = allLogogramsInGame
                .filter { it != h.getIdeogramma(languageCode) }
                .shuffled()
                .distinct()
                .take(3)

            val options = (distractors + h.getIdeogramma(languageCode))
                .distinct()
                .shuffled()
                .map { pron ->
                    Option(
                        text = pron,
                        correct = pron == h.getIdeogramma(languageCode)
                    )
                }

            SingleSelectionContent(
                text = Res.string.quiz_hiero_logogramma,
                detail = h.gardinerCode,
                options = options,
            )
        }
    }
}

/**
 * Estrae i codici Gardiner dei geroglifici presenti nel contenuto di una lezione hiero.
 * Utile per passare i codici "già visti" alle lezioni successive.
 */
fun extractHieroCodes(hieroContent: List<IExplanation>): List<String> =
    hieroContent.filterIsInstance<UniliteralExplanation>().map { it.id }
